import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ErrorCodes } from '@ringana/errors';
import { EHttpMethod } from '../common/enums/fetch.enum';
import ErrorHandlerHelper from './ErrorHandlerHelper';
import ApiRoutes from '../api/ApiRoutes';
import ValidationError from '../errors/validation-error';
import ApplicationError from '../errors/application-error';
import { EErrorMessage } from '../common/enums/messages.enum';
import FetchError from '../errors/fetch-error';
import {
  IApiErrorResponseData,
  IApiRequestObject,
  IApiRequestParams,
} from '../common/interfaces/api.interface';
import { TApiResponse } from '../common/types/api.types';

export default class ApiHelper {
  public static handleKnownApiError(err: AxiosError, apiRoute: ApiRoutes): EErrorMessage {
    const response = err.response as AxiosResponse<IApiErrorResponseData>;
    if(!response) {
      throw new FetchError(ErrorCodes.FETCH_NO_RESPONSE, err, apiRoute);
    }

    const errorCode: ErrorCodes = response?.data?.error ?? ErrorCodes.UNKNOWN;
    switch(errorCode) {
      case ErrorCodes.TAX_NUM_VERIFICATION_CAPTCHA_WRONG:
        throw new ApplicationError(EErrorMessage.CAPTCHA_WRONG);
      case ErrorCodes.VALIDATION_FAILED:
      case ErrorCodes.VALIDATION_PARAMETER_FAILED:
        throw new ValidationError(errorCode, err);
      case ErrorCodes.UNAUTHORIZED:
        throw new ApplicationError(EErrorMessage.UNAUTHORIZED, { err, errorCode });
      case ErrorCodes.NOT_FOUND:
        throw new ApplicationError(EErrorMessage.NOT_FOUND, { err, errorCode });
      case ErrorCodes.ROUTE_NOT_FOUND:
        throw new ApplicationError(EErrorMessage.ROUTE_NOT_FOUND, { err, errorCode });
      case ErrorCodes.INFOWORX_RESPONSE_CONFIRM_NEWSLETTER:
        throw new ApplicationError(EErrorMessage.INFOWORX_RESPONSE_CONFIRM_NEWSLETTER, { err, errorCode });
      case ErrorCodes.ANNOUNCEMENT_CALENDAR_DOOR_TOO_EARLY:
        throw new ApplicationError(EErrorMessage.FORBIDDEN, { err, errorCode });
      case ErrorCodes.ANNOUNCEMENT_CALENDAR_DOOR_TOO_LATE:
        throw new ApplicationError(EErrorMessage.GONE, { err, errorCode });
      default:
        throw new FetchError(ErrorCodes.FETCH_ERROR, err, apiRoute);
    }
  }

  // Typescript says that T is not assignable to ISalutation[] even though its declared that way in api.types.ts.
  // Therefore ignoring this for now.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  public static cleanResponseData<T>(res: TApiResponse<T>): TApiResponse<T> {
    if(!res.data) {
      return res;
    }
    if(typeof res.data === `string`) {
      (res.data as string) = (res.data as string).replace(new RegExp(/<\/?br ?\/?> ?/gi), ``);
      return res;
    }
    for(const key of Object.keys(res.data as Record<string, unknown>)) {
      if(typeof res.data[key as keyof T] === `string`) {
        (res.data[key as keyof T] as unknown as string) = (res.data[key as keyof T] as unknown as string).replace(new RegExp(/<\/?br ?\/?> ?/gi), ``);
      }
    }
    return res;
  }

  public static getRequestConfig(route: ApiRoutes, object: IApiRequestObject): AxiosRequestConfig {
    const { method } = route;
    const requestConfig: AxiosRequestConfig = {
      data: method === EHttpMethod.GET ? undefined : object.data,
      method,
      params: method === EHttpMethod.GET ? object.data : undefined,
      responseType: object.axiosConfig?.responseType || `json`,
      url: object.params ? ApiHelper.transformParamsForURL(route.url, object.params) : route.url,
    };
    if(object.formData) {
      requestConfig.headers = { 'Content-Type': `multipart/form-data` };
    }
    return requestConfig;
  }

  private static transformParamsForURL(url: string, params: IApiRequestParams): string {
    let ret = url;

    for(const param of Object.keys(params)) {
      const value = params[param]?.toString();
      if(!ret.includes(`:${param}`)) {
        ErrorHandlerHelper.handleError(`Parameter ":${param}" is NOT part of route "${url}"`, { logToConsole: true });
      }
      ret = ret.replace(RegExp(`:${param}\\b`), value || ``);
    }

    return ret;
  }
}
